import React, { useState, useEffect, useRef } from 'react';
import { generateClient } from 'aws-amplify/data';
import { useAuth } from '../../contexts/AuthContext';
import MemberNav from './MemberNav';
import { DashboardSkeleton } from './LoadingSkeleton';
import MembershipCard from './MembershipCard';
import SeasonStatsCard from './SeasonStatsCard';
import TideWeatherWidget from './TideWeatherWidget';
import AchievementsBadges from './AchievementsBadges';
import MyHappySnapsCard from './MyHappySnapsCard';
import CheckInModal from './CheckInModal';
import { getRenewalStatusDisplay, applyOnlineRenewal } from '../../utils/membership';
import { CatchRecord, speciesLabel, computePersonalSpeciesBests } from '../../utils/memberStats';
import { logError } from '../../utils/logger';
import {
  computeSeasonStandings,
  findMemberRank,
  findMemberPoints,
} from '../../utils/championPoints';
import PaymentChoice from '../payment/PaymentChoice';
import PaymentReconciliationNotice from '../payment/PaymentReconciliationNotice';
import { membershipTypeToFeeKey, getFeeCents } from '@shared/fees';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { getPreRegistrationWindowState, getPreRegistrationOpensAt } from '@shared/competitions';
import { deriveLifecycle, describeLifecycle, isPublished } from '@shared/competitionRules';
import { formatClubDate, formatClubTime } from '../../utils/clubTime';
import { clubYear } from '@shared/membership';
import { isFamilyDependant } from '@shared/householdRenewal';
import AddFamilyMemberDialog from './family/AddFamilyMemberDialog';
import RequestRemovalDialog from './family/RequestRemovalDialog';
import RequestPortalAccessDialog from './family/RequestPortalAccessDialog';
import FamilyRequestsList from './family/FamilyRequestsList';
import { myFamilyChangeRequests, type MyFamilyChangeRequest } from './family/familyChangeClient';

const client = generateClient({ authMode: 'userPool' });

interface MemberDashboardProps {
  onNavigate: (
    page:
      | 'dashboard'
      | 'profile'
      | 'competitions'
      | 'catches'
      | 'my-stats'
      | 'my-fishing'
      | 'season-championship'
      | 'home'
  ) => void;
}

const MemberDashboard: React.FC<MemberDashboardProps> = ({ onNavigate }) => {
  const { userId } = useAuth();

  const [member, setMember] = useState<any>(null);
  const [renewal, setRenewal] = useState<{
    canRenew: boolean;
    amountCents: number | null;
    renewsFamily: boolean;
    familyCount: number;
  } | null>(null);
  const [competitions, setCompetitions] = useState<any[]>([]);
  const [myRegistrations, setMyRegistrations] = useState<any[]>([]);
  const [eventTitleMap, setEventTitleMap] = useState<Record<string, string>>({});
  // Register-button state for the Upcoming Competitions card below — mirrors
  // CompetitionsList.tsx's own local toast/registering pattern exactly, so both
  // Register buttons behave identically instead of drifting (this one previously
  // had no loading state, no eligibility check, and no failure feedback at all).
  const [registeringEventId, setRegisteringEventId] = useState<string | null>(null);
  const [regToast, setRegToast] = useState<{ message: string; type: 'success' | 'error' } | null>(
    null
  );
  const showRegToast = (message: string, type: 'success' | 'error') => {
    setRegToast({ message, type });
    setTimeout(() => setRegToast(null), 5000);
  };
  const [allCatches, setAllCatches] = useState<any[]>([]);
  const [recentClubCatches, setRecentClubCatches] = useState<any[]>([]);
  const [placements, setPlacements] = useState<Array<{ species: string; position: number }>>([]);
  const [catchLoadError, setCatchLoadError] = useState<string | null>(null);
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [seasonStandings, setSeasonStandings] = useState<{
    seniorStandings: any[];
    juniorStandings: any[];
  }>({ seniorStandings: [], juniorStandings: [] });
  const [familyMembersList, setFamilyMembersList] = useState<any[]>([]);
  const [familyResults, setFamilyResults] = useState<
    Record<
      string,
      {
        catchCount: number;
        bests: Array<{ species: string; lengthCm: number }>;
        points: number;
        rank: number | null;
      }
    >
  >({});
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | undefined>(undefined);
  const [checkInEventId, setCheckInEventId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showRenewalForm, setShowRenewalForm] = useState(false);
  // Backlog M10. Set when processPayment charged the card but its own
  // record-write/membership-update failed afterwards — see
  // SquarePaymentForm's onReconciliationRequired doc comment. Distinct from
  // the normal success path so the member is told explicitly rather than
  // shown an identical "you're all set" screen for a payment that may not
  // actually be recorded.
  const [renewalReconciliationRef, setRenewalReconciliationRef] = useState<string | null>(null);

  // #127 slice 5 — family change requests (R6, R10): "Add a family member",
  // "Request removal" and the status/consent list.
  const [showAddFamilyMember, setShowAddFamilyMember] = useState(false);
  const [removalTarget, setRemovalTarget] = useState<any>(null);
  // #150 slice 2 (R6, design §6) — "Ask for a login" for a family member
  // with no cognitoSub. Focus returns to the button that opened the dialog.
  const [portalAccessTarget, setPortalAccessTarget] = useState<any>(null);
  const portalAccessButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [familyRequests, setFamilyRequests] = useState<MyFamilyChangeRequest[]>([]);
  const [familyActionToast, setFamilyActionToast] = useState<string | null>(null);

  // Fetch member data on mount
  useEffect(() => {
    fetchMemberData();
  }, [userId]);

  // Read ?checkin=<eventId> from URL once on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('checkin');
    if (id) setCheckInEventId(id);
  }, []);

  // Resolve profile photo URL when member (or its S3 key) changes
  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      const key = member?.profilePhotoS3Key;
      if (!key) {
        setProfilePhotoUrl(undefined);
        return;
      }
      try {
        const r: any = await (client.queries as any).generateDownloadUrl({ s3Key: key });
        const d = parseLambdaResponse(r?.data);
        if (!cancelled && d?.downloadUrl) setProfilePhotoUrl(d.downloadUrl);
      } catch {
        /* silent */
      }
    };
    resolve();
    return () => {
      cancelled = true;
    };
  }, [member?.profilePhotoS3Key]);

  // #127 slice 5 — loads the caller's own family change requests plus any
  // awaiting their consent. Non-critical: a failure leaves the list empty
  // rather than failing the whole dashboard.
  const loadFamilyRequests = async () => {
    try {
      const result = await myFamilyChangeRequests();
      if (result.success) setFamilyRequests(result.requests || []);
    } catch {
      /* non-critical */
    }
  };

  const fetchMemberData = async () => {
    if (!userId) {
      setLoadError('User not authenticated');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setLoadError(null);

      // Own profile + role-aware family roster, via the narrow server-side
      // projection (replaces direct Member.list/membersByFamilyGroup reads,
      // which relied on the model-level allow.authenticated() grant that
      // exposed every field on every member's record to any signed-in user).
      const profileResult: any = await (client.queries as any).getOwnMemberProfile({});
      const profileResponse = parseLambdaResponse(profileResult?.data);

      if (!profileResponse?.success) {
        logError('MemberDashboard', [{ message: profileResponse?.error || 'Unknown error' }]);
        setLoadError(profileResponse?.error || 'Failed to load dashboard data');
        setIsLoading(false);
        return;
      }

      const m = profileResponse.own;
      if (!m) {
        setLoadError('Member profile not found');
        setIsLoading(false);
        return;
      }

      setMember(m);
      // #127 slice 6 (R7, contract point 7/9) — the renewal info the dashboard
      // needs is computed server-side and returned alongside the profile.
      // Built in parallel by another agent; if it's ever missing (e.g. an
      // older cached response), fall back to the pre-slice-6 behaviour: a
      // family dependant can't renew, and the amount is the flat fee.
      if (profileResponse.renewal) {
        setRenewal(profileResponse.renewal);
      } else {
        const isDependant = m.membershipType === 'FAMILY' && !!m.familyGroupId && m.familyPrimary !== true;
        const canRenew =
          m.status === 'VERIFIED' &&
          ['PENDING_RENEWAL', 'EXPIRED', 'LAPSED'].includes(m.renewalStatus) &&
          !isDependant;
        const feeKey = membershipTypeToFeeKey(m.membershipType) ?? 'membership_fee_adult';
        const amountCents = getFeeCents(feeKey) ?? 4000;
        setRenewal({ canRenew, amountCents, renewsFamily: false, familyCount: 1 });
      }
      setFamilyMembersList(profileResponse.family || []);
      // #127 slice 5 — the caller's own family change requests, plus any
      // awaiting their consent. Non-blocking: a failure here shouldn't stop
      // the rest of the dashboard loading.
      loadFamilyRequests();
      // Local copy for use later in THIS same loadData pass. The
      // familyMembersList state above will not have updated yet, which is what
      // the now-fixed `familyMembersLocal` reference below was reaching for.
      const familyLocal: any[] = profileResponse.family || [];

      // Fetch upcoming competitions
      const now = new Date().toISOString();
      const { data: compsData } = await client.models.Event.list({
        filter: {
          eventType: { eq: 'COMPETITION' },
          eventDate: { gt: now },
          isActive: { eq: true },
        },
      });
      const sortedComps = (compsData || [])
        .sort((a: any, b: any) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime())
        .slice(0, 3);
      setCompetitions(sortedComps);

      // Fetch my pre-registrations
      const { data: regsData } = await client.models.EventPreRegistration.list({
        filter: { memberSub: { eq: userId } },
      });
      setMyRegistrations(regsData || []);

      // Resolve event titles for registrations
      const eventMap: Record<string, string> = {};
      for (const c of compsData || []) eventMap[c.id] = c.title;
      for (const reg of regsData || []) {
        if (!eventMap[reg.eventId]) {
          try {
            const ev = await client.models.Event.get({ id: reg.eventId });
            if (ev.data?.title) eventMap[reg.eventId] = ev.data.title;
          } catch {
            /* ignore */
          }
        }
      }
      setEventTitleMap(eventMap);

      // Fetch catches
      try {
        let fetchedCatches: any[] = [];
        let nextToken: string | null = null;
        do {
          const result: any = await client.models.CatchSubmission.list({
            filter: { memberSub: { eq: userId } },
            ...(nextToken ? { nextToken } : {}),
          });
          fetchedCatches = fetchedCatches.concat(result.data || []);
          nextToken = result.nextToken ?? null;
        } while (nextToken);
        const approved = fetchedCatches.filter((c: any) => c.status === 'APPROVED');
        setAllCatches(approved);

        // Fetch placements from published competitions
        const publishedComps = (compsData || []).filter(
          (c: any) =>
            isPublished(c) && approved.some((a: any) => a.eventId === c.id)
        );
        const allPlacements: Array<{ species: string; position: number }> = [];
        await Promise.allSettled(
          publishedComps.map(async (comp: any) => {
            try {
              const lb = await (client.queries as any).calculateLeaderboard({ eventId: comp.id });
              const data = parseLambdaResponse(lb?.data);
              const memberName = `${m.firstName} ${m.lastName}`;
              for (const [species, entries] of Object.entries(data?.categoryRankings || {})) {
                const idx = (entries as any[]).findIndex(
                  (e: any) => e.competitorName === memberName
                );
                if (idx >= 0) allPlacements.push({ species, position: idx + 1 });
              }
            } catch {
              /* skip */
            }
          })
        );
        setPlacements(allPlacements);
      } catch (catchErr) {
        setCatchLoadError('Could not load catch data');
      }

      // Fetch recent club-wide catches (latest 5 approved). #79: a filtered
      // list({ filter, limit: 20 }) reads one page and filters/limits it
      // afterwards, so it can silently return fewer than 20 scanned rows and
      // miss the true most-recent catches. Page fully, sort by time, then
      // take the 5 this screen actually displays.
      try {
        let recentCatches: any[] = [];
        let recentToken: string | null = null;
        do {
          const page: any = await client.models.CatchSubmission.list({
            filter: { status: { eq: 'APPROVED' } },
            ...(recentToken ? { nextToken: recentToken } : {}),
          });
          recentCatches = recentCatches.concat(page.data || []);
          recentToken = page.nextToken ?? null;
        } while (recentToken);
        const sorted = recentCatches
          .sort(
            (a: any, b: any) =>
              new Date(b.submissionTimestamp || 0).getTime() -
              new Date(a.submissionTimestamp || 0).getTime()
          )
          .slice(0, 5);
        setRecentClubCatches(sorted);
      } catch {
        /* non-critical */
      }

      // Fetch active announcements. #79-class: page fully — a filtered
      // list() caps rows SCANNED, not returned, so the top-3 could silently
      // miss a more recent active announcement.
      try {
        let allAnnouncements: any[] = [];
        let annToken: string | null = null;
        do {
          const page: any = await client.models.Announcement.list({
            filter: { isActive: { eq: true } },
            ...(annToken ? { nextToken: annToken } : {}),
          });
          allAnnouncements = allAnnouncements.concat(page.data || []);
          annToken = page.nextToken ?? null;
        } while (annToken);
        const sorted = allAnnouncements
          .sort((a: any, b: any) => (b.publishDate || '').localeCompare(a.publishDate || ''))
          .slice(0, 3);
        setAnnouncements(sorted);
      } catch {
        /* non-critical */
      }

      // Load season champion standings from published competitions.
      // #134: no server-side stored competition state filter any more — the "no
      // dual-write" direction means lists filter by the decision field
      // client-side with isPublished (computeSeasonStandings does this).
      // #79-class: page fully — a filtered list() caps rows SCANNED, not
      // returned, so a published competition's champion could silently be
      // missing from the standings.
      try {
        let allComps: any[] = [];
        let compsToken: string | null = null;
        do {
          const page: any = await client.models.Event.list({
            filter: { eventType: { eq: 'COMPETITION' } },
            selectionSet: [
              'id',
              'eventDate',
              'publishedAt',
              'seniorChampionName',
              'seniorChampionPoints',
              'seniorChampionMemberSub',
              'juniorChampionName',
              'juniorChampionPoints',
              'juniorChampionMemberSub',
            ],
            ...(compsToken ? { nextToken: compsToken } : {}),
          });
          allComps = allComps.concat(page.data || []);
          compsToken = page.nextToken ?? null;
        } while (compsToken);
        const standings = computeSeasonStandings(allComps || [], new Date().getFullYear());
        setSeasonStandings(standings);

        // Family results rollup — adult/PRIMARY viewers only. A junior
        // viewing their own dashboard never triggers this fetch at all
        // (not just hidden in the UI), per the committee-directed
        // asymmetric visibility rule.
        if (!m.isJunior && familyLocal.length > 0) {
          const resultsEntries = await Promise.allSettled(
            familyLocal.map(async (fm: any) => {
              // Query by memberId, not memberSub, so a family member with no
              // Cognito login (no cognitoSub) still has their results found —
              // catches submitted on their behalf are recorded against their
              // memberId (see family-member-accounts Requirement 11).
              // #79-class: page fully — a filtered list() caps rows
              // SCANNED, not returned, so a single call could silently
              // undercount a family member's catches.
              let fmCatches: any[] = [];
              let fmToken: string | null = null;
              do {
                const page: any = await client.models.CatchSubmission.list({
                  filter: { memberId: { eq: fm.id }, status: { eq: 'APPROVED' } },
                  ...(fmToken ? { nextToken: fmToken } : {}),
                });
                fmCatches = fmCatches.concat(page.data || []);
                fmToken = page.nextToken ?? null;
              } while (fmToken);
              const approvedFmCatches = fmCatches;
              const bests = computePersonalSpeciesBests(approvedFmCatches as CatchRecord[]);
              // Points/rank lookups are memberSub-keyed; a family member with
              // no cognitoSub simply won't be found (findMemberPoints/Rank
              // return 0/null) — an accepted, pre-existing limitation, not a
              // regression introduced here.
              const fmStandings = fm.isJunior
                ? standings.juniorStandings
                : standings.seniorStandings;
              const points = fm.cognitoSub ? findMemberPoints(fmStandings, fm.cognitoSub) : 0;
              const rank = fm.cognitoSub ? findMemberRank(fmStandings, fm.cognitoSub) : null;
              return [
                fm.id,
                { catchCount: approvedFmCatches.length, bests, points, rank },
              ] as const;
            })
          );
          const resultsMap: Record<
            string,
            {
              catchCount: number;
              bests: Array<{ species: string; lengthCm: number }>;
              points: number;
              rank: number | null;
            }
          > = {};
          for (const entry of resultsEntries) {
            if (entry.status === 'fulfilled') {
              const [fmId, fmResult] = entry.value;
              resultsMap[fmId] = fmResult;
            }
          }
          setFamilyResults(resultsMap);
        }
      } catch {
        /* non-critical */
      }

      setIsLoading(false);
    } catch (error) {
      logError('MemberDashboard', error);
      setLoadError('An error occurred while loading your dashboard');
      setIsLoading(false);
    }
  };
  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MemberNav currentPage="dashboard" onNavigate={onNavigate} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <DashboardSkeleton />
        </div>
      </div>
    );
  }

  // Error state
  if (loadError || !member) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MemberNav currentPage="dashboard" onNavigate={onNavigate} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-800">{loadError || 'Failed to load dashboard'}</p>
            <button
              onClick={fetchMemberData}
              className="mt-4 text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <MemberNav currentPage="dashboard" onNavigate={onNavigate} />

      {regToast && (
        <div
          role="alert"
          aria-live="assertive"
          className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm transition-all max-w-sm ${
            regToast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {regToast.message}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* BAND 1 — IDENTITY: welcome + themed membership card */}
        <div className="flex flex-col gap-6 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Welcome back, {member.firstName} {member.lastName}!
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {member.memberNumber ? `Member ${member.memberNumber}` : 'Member'} ·{' '}
              {(member.membershipType || '').replace(/_/g, ' ')}
            </p>
          </div>
          <div>
            <MembershipCard
              member={{
                firstName: member.firstName,
                lastName: member.lastName,
                memberNumber: member.memberNumber || 'Pending',
                membershipType: member.membershipType,
                status: member.status,
                profilePhotoS3Key: member.profilePhotoS3Key,
                email: member.email,
                membershipYear: member.membershipYear,
                membershipExpiryDate: member.membershipExpiryDate,
                renewalStatus: member.renewalStatus,
                isCommittee: member.isCommittee,
              }}
              profilePhotoUrl={profilePhotoUrl}
            />
          </div>
        </div>

        {/* Unpaid membership fee banner. Not for a family dependant: the
            family's fee is paid by the primary (#127). */}
        {member.status === 'VERIFIED' && !member.paymentReceived && !isFamilyDependant(member) && !renewal?.canRenew && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4">
            {showPaymentForm ? (
              (() => {
                const feeKey =
                  membershipTypeToFeeKey(member.membershipType) ?? 'membership_fee_adult';
                const amountCents = getFeeCents(feeKey) ?? 4000;
                return (
                  <PaymentChoice
                    amountCents={amountCents}
                    amountDisplay={`$${(amountCents / 100).toFixed(2)}`}
                    paymentType="MEMBERSHIP_NEW"
                    entityId={member.id}
                    entityType="MEMBER"
                    payerEmail={member.email ?? ''}
                    payerName={`${member.firstName} ${member.lastName}`}
                    feeKey={feeKey}
                    authMode="userPool"
                    onSuccess={() => {
                      setMember((m: any) => ({ ...m, paymentReceived: true }));
                      setShowPaymentForm(false);
                    }}
                    onPayOnDay={() => setShowPaymentForm(false)}
                  />
                );
              })()
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-amber-800">💳 Membership fee outstanding</p>
                  <p className="text-sm text-amber-700">
                    Your membership fee has not yet been paid.
                  </p>
                </div>
                <button
                  onClick={() => setShowPaymentForm(true)}
                  className="px-4 py-2 bg-fishing-blue text-white rounded font-medium text-sm hover:bg-blue-700 whitespace-nowrap"
                >
                  Pay Membership Fee
                </button>
              </div>
            )}
          </div>
        )}

        {/* Payment reconciliation notice — see PaymentReconciliationNotice's doc
            comment. Shown in place of the normal renewal banner so it cannot
            be missed. */}
        {renewalReconciliationRef && (
          <PaymentReconciliationNotice paymentReference={renewalReconciliationRef} />
        )}

        {/* Renewal payment banners. renewal.canRenew comes from the server
            (VERIFIED, renewal due, not a dependant), the same checks
            process-payment applies; the member's own paymentReceived flag is
            not one of them (found on dev 10 Oct 2026). */}
        {!renewalReconciliationRef &&
          member.status === 'VERIFIED' &&
          renewal?.canRenew && (
            <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 mb-4">
              {showRenewalForm ? (
                (() => {
                  const feeKey =
                    membershipTypeToFeeKey(member.membershipType) ?? 'membership_fee_adult';
                  const amountCents = renewal.amountCents ?? getFeeCents(feeKey) ?? 4000;
                  return (
                    <PaymentChoice
                      amountCents={amountCents}
                      amountDisplay={`$${(amountCents / 100).toFixed(2)}`}
                      paymentType="MEMBERSHIP_RENEWAL"
                      entityId={member.id}
                      entityType="MEMBER"
                      payerEmail={member.email ?? ''}
                      payerName={`${member.firstName} ${member.lastName}`}
                      feeKey={feeKey}
                      authMode="userPool"
                      onSuccess={() => {
                        // #108: CURRENT for the year, same as process-payment.
                        setMember((m: any) => applyOnlineRenewal(m));
                        setShowRenewalForm(false);
                        // #127 slice 6 — reload so the whole family shows CURRENT.
                        fetchMemberData();
                      }}
                      onReconciliationRequired={(paymentId) => {
                        setRenewalReconciliationRef(paymentId);
                        setShowRenewalForm(false);
                      }}
                      onPayOnDay={() => setShowRenewalForm(false)}
                    />
                  );
                })()
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-blue-800">🔄 Membership renewal due</p>
                    <p className="text-sm text-blue-700">
                      {renewal.renewsFamily
                        ? `Renew your family's membership for ${clubYear()}: ${renewal.familyCount} people, $${((renewal.amountCents ?? 0) / 100).toFixed(2)}.`
                        : 'Renew now to keep your membership active.'}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowRenewalForm(true)}
                    className="px-4 py-2 bg-fishing-blue text-white rounded font-medium text-sm hover:bg-blue-700 whitespace-nowrap"
                  >
                    Renew Online
                  </button>
                </div>
              )}
            </div>
          )}

        {/* #127 slice 6 (R7, contract point 9) — a family dependant can't
            renew themselves: the family primary renews the whole family. */}
        {/* A dependant's own paymentReceived is irrelevant here (the primary
            pays for the family), so it is deliberately not checked. */}
        {!renewalReconciliationRef &&
          member.status === 'VERIFIED' &&
          !renewal?.canRenew &&
          ['PENDING_RENEWAL', 'EXPIRED', 'LAPSED'].includes(member.renewalStatus) &&
          member.membershipType === 'FAMILY' &&
          !!member.familyGroupId &&
          member.familyPrimary !== true && (
            <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-700" data-testid="renewal-via-primary">
                Your family primary renews the whole family's membership.
              </p>
            </div>
          )}

        {/* Already-paid guard */}
        {member.status === 'VERIFIED' &&
          member.paymentReceived &&
          member.renewalStatus === 'CURRENT' &&
          showPaymentForm && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 text-green-800 text-sm font-medium">
              ✅ Your membership is current — no payment is needed at this time.
            </div>
          )}

        {/* Renewal banner — part of Identity band */}
        {member.renewalStatus &&
          (() => {
            const display = getRenewalStatusDisplay(member.renewalStatus, member.membershipYear);
            return (
              <div
                className={`${display.bgColour} border rounded-lg p-4 mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-block w-3 h-3 rounded-full ${
                      member.renewalStatus === 'CURRENT'
                        ? 'bg-green-500'
                        : member.renewalStatus === 'PENDING_RENEWAL'
                          ? 'bg-amber-500'
                          : 'bg-red-500'
                    }`}
                  />
                  <span className={`font-medium ${display.colour}`}>{display.text}</span>
                </div>
                <div className="flex items-center gap-3">
                  {display.showRenewButton && !isFamilyDependant(member) && (
                    <span className="text-sm text-gray-700">
                      Pay your renewal fee at the next competition day — an admin will confirm your
                      payment.
                    </span>
                  )}
                  {member.renewalStatus === 'LAPSED' && (
                    <a
                      href="mailto:fishing@tumbyfc.com"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      📧 Contact Admin
                    </a>
                  )}
                </div>
              </div>
            );
          })()}

        {/* BAND 2 — ACTIONABLE: next comp, registrations, announcements, tide/weather */}
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          What's happening
        </h2>

        {/* Next Competition Countdown */}
        {(() => {
          const now = Date.now();
          const next = competitions.find(
            (c: any) =>
              c.eventType === 'COMPETITION' && new Date(c.eventDate).getTime() >= now - 86400000
          );
          if (!next)
            return (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">🏆 Next Competition</h3>
                <p className="text-sm text-gray-500">
                  No upcoming competitions scheduled. Check back soon!
                </p>
              </div>
            );
          const daysToGo = Math.ceil(
            (new Date(next.eventDate).getTime() - now) / (1000 * 60 * 60 * 24)
          );
          const countdown =
            daysToGo <= 0
              ? 'Today! 🎣'
              : daysToGo === 1
                ? 'Tomorrow! 🎣'
                : `${daysToGo} days to go ⏳`;
          return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">🏆 Next Competition</h3>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-lg font-bold text-gray-900">{next.title}</p>
                  <p className="text-sm text-gray-500">
                    {formatClubDate(next.eventDate, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                    {next.location ? ` · ${next.location}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold text-fishing-blue">{countdown}</span>
                  {next.preRegistrationEnabled && (
                    <button
                      onClick={() => onNavigate('competitions')}
                      className="px-4 py-2 text-sm font-medium text-white bg-fishing-blue hover:bg-blue-700 rounded-md"
                    >
                      Pre-Register
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* My Registrations + Upcoming Competitions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">My Registrations</h3>
            {myRegistrations.length > 0 ? (
              <div className="space-y-3">
                {myRegistrations.map((reg: any) => (
                  <div
                    key={reg.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {eventTitleMap[reg.eventId] || 'Competition'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatClubDate(reg.registrationDate)}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        reg.status === 'APPROVED'
                          ? 'bg-green-100 text-green-800'
                          : reg.status === 'PENDING'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {reg.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">No registrations yet</p>
                <button
                  onClick={() => onNavigate('competitions')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-fishing-blue hover:bg-blue-700"
                >
                  Browse Competitions
                </button>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Upcoming Competitions</h3>
              <button
                onClick={() => onNavigate('competitions')}
                className="text-sm text-fishing-blue hover:text-blue-700 font-medium"
              >
                View All →
              </button>
            </div>
            {competitions.length > 0 ? (
              <div className="space-y-3">
                {competitions.map((comp: any) => {
                  const isRegistered = myRegistrations.some((r: any) => r.eventId === comp.id);
                  return (
                    <div
                      key={comp.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">{comp.title}</p>
                        <p className="text-xs text-gray-500">
                          {formatClubDate(comp.eventDate, {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                          {comp.location ? ` · ${comp.location}` : ''}
                        </p>
                      </div>
                      {isRegistered ? (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Registered
                        </span>
                      ) : (
                        (() => {
                          // Eligibility gate mirrors CompetitionsList.tsx's getButtonState
                          // exactly (same shared helpers, same order of checks), fixing three
                          // real bugs in this button:
                          //  1. It previously had NO eligibility check at all — it rendered
                          //     as a live "Register" button even when pre-registration was
                          //     disabled/closed/not-yet-open, and only failed silently on
                          //     click (server-side rejection with no user-facing feedback).
                          //  2. No loading state — nothing stopped a slow network from
                          //     allowing a double-click to fire two requests.
                          //  3. Failures were swallowed into logError(), which is a no-op in
                          //     production (utils/logger.ts) — a member saw nothing at all
                          //     happen on click, with no way to know why.
                          // When online pre-registration isn't available, day-of
                          // check-in may still be open *right now* — it opens on the
                          // same 24h window and runs through registrationCloseTime
                          // (the morning of the comp), so on the eve of an event a
                          // member can register online this minute. Saying "Day-of
                          // Check-In Only" in that state sends them away for no
                          // reason, which is exactly what shipped in the first cut of
                          // this fix. Reuse deriveLifecycle — the existing
                          // check-in source of truth — rather than adding a third
                          // window implementation (see BACKLOG M0e).
                          const renderCheckInFallback = (fallbackLabel: string) => {
                            const l = deriveLifecycle(comp, new Date());
                            const { signOn } = describeLifecycle(comp, new Date(), formatClubTime);
                            if (l.signOnOpen) {
                              return (
                                <button
                                  onClick={() => {
                                    window.location.href = '/check-in';
                                  }}
                                  title={signOn}
                                  className="px-3 py-1.5 text-xs font-medium text-white bg-fishing-blue hover:bg-blue-700 rounded-md"
                                >
                                  Register now →
                                </button>
                              );
                            }
                            return (
                              <span
                                className="px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 rounded-md"
                                title={signOn}
                              >
                                {fallbackLabel}
                              </span>
                            );
                          };

                          if (!comp.preRegistrationEnabled) {
                            return renderCheckInFallback('Day-of Check-In Only');
                          }
                          // Closes at the event's sign-on cutoff
                          // (registrationCloseTime, defaulting to fishing
                          // start + 2h under the canonical rule), not at
                          // eventDate itself — see
                          // shared/competitions.ts getPreRegistrationClosesAt.
                          const windowState = getPreRegistrationWindowState(
                            comp.eventDate,
                            new Date(),
                            comp.registrationCloseTime
                          );
                          if (windowState === 'NO_DATE') {
                            return renderCheckInFallback('Day-of Check-In Only');
                          }
                          if (windowState === 'NOT_YET_OPEN') {
                            const opensAt = getPreRegistrationOpensAt(comp.eventDate)!;
                            return (
                              <span className="px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 rounded-md">
                                Opens{' '}
                                {formatClubDate(opensAt, {
                                  weekday: 'short',
                                  day: 'numeric',
                                  month: 'short',
                                })}
                              </span>
                            );
                          }
                          if (windowState === 'CLOSED') {
                            // Pre-registration has closed, but day-of check-in
                            // typically remains open until the morning of the comp.
                            return renderCheckInFallback('Registration Closed');
                          }

                          const isRegistering = registeringEventId === comp.id;
                          return (
                            <button
                              onClick={async () => {
                                // Additive narrow API (see .kiro/specs/BACKLOG.md,
                                // 18 Jul 2026 security remediation) — replaces the
                                // owner-generated EventPreRegistration.create.
                                setRegisteringEventId(comp.id);
                                try {
                                  const result: any = await (
                                    client.mutations as any
                                  ).createOwnEventPreRegistration({
                                    eventId: comp.id,
                                  });
                                  const response = parseLambdaResponse<{
                                    success?: boolean;
                                    preRegistrationId?: string;
                                    message?: string;
                                  }>(result?.data);
                                  if (!response?.success) {
                                    logError(
                                      'MemberDashboard',
                                      response?.message || 'createOwnEventPreRegistration failed'
                                    );
                                    showRegToast(
                                      response?.message ||
                                        'Could not register for this competition. Please try again.',
                                      'error'
                                    );
                                    return;
                                  }
                                  setMyRegistrations((prev: any[]) => [
                                    ...prev,
                                    {
                                      eventId: comp.id,
                                      status: 'PENDING',
                                      registrationDate: new Date().toISOString(),
                                    },
                                  ]);
                                  setEventTitleMap((prev) => ({ ...prev, [comp.id]: comp.title }));
                                  showRegToast('Registered! See Competitions for payment options.', 'success');
                                } catch (err) {
                                  logError('MemberDashboard', err);
                                  showRegToast(
                                    'An error occurred. Please try again.',
                                    'error'
                                  );
                                } finally {
                                  setRegisteringEventId(null);
                                }
                              }}
                              disabled={isRegistering}
                              className="px-3 py-1.5 text-xs font-medium text-white bg-fishing-blue hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed rounded-md"
                            >
                              {isRegistering ? 'Registering…' : 'Register'}
                            </button>
                          );
                        })()
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No upcoming competitions</p>
              </div>
            )}
          </div>
        </div>

        {/* Announcements (compact, first 3) */}
        {announcements.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">📢 Announcements</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {announcements.slice(0, 3).map((ann: any) => {
                const badgeColours: Record<string, string> = {
                  URGENT: 'bg-red-100 text-red-800',
                  CLUB_NEWS: 'bg-blue-100 text-blue-800',
                  TIDE_REPORT: 'bg-teal-100 text-teal-800',
                  FISHING_TIPS: 'bg-green-100 text-green-800',
                  GENERAL: 'bg-gray-100 text-gray-800',
                };
                const badge = badgeColours[ann.category] || badgeColours.GENERAL;
                const excerpt =
                  ann.excerpt ||
                  (ann.content?.length > 120 ? ann.content.slice(0, 120) + '…' : ann.content);
                return (
                  <div
                    key={ann.id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${badge}`}>
                        {(ann.category || 'GENERAL').replace(/_/g, ' ')}
                      </span>
                      <span className="text-xs text-gray-400">
                        {ann.publishDate
                          ? formatClubDate(ann.publishDate)
                          : ''}
                      </span>
                    </div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">{ann.title}</h4>
                    <p className="text-sm text-gray-600">{excerpt}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* BAND 3 — MY FISHING: season stats + personal bests + champion points + catches summary */}
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          My fishing
        </h2>

        {catchLoadError && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
            <p className="text-sm text-yellow-800">
              Could not load catch data. Stats and badges may be incomplete.
            </p>
          </div>
        )}

        <SeasonStatsCard
          catches={allCatches as CatchRecord[]}
          membershipType={member.membershipType || ''}
          familyMembers={(() => {
            try {
              return typeof member.familyMembers === 'string'
                ? JSON.parse(member.familyMembers)
                : member.familyMembers || [];
            } catch {
              return [];
            }
          })()}
          familyCatches={allCatches as CatchRecord[]}
        />

        {/* Personal bests — horizontal scroll row */}
        {allCatches.length > 0 &&
          (() => {
            const bests = computePersonalSpeciesBests(allCatches as CatchRecord[]);
            if (bests.length === 0) return null;
            return (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">🐟 Personal Bests</h3>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {bests.map((b) => (
                    <div
                      key={b.species}
                      className="flex-shrink-0 bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3 text-center min-w-[120px]"
                    >
                      <div className="text-lg mb-1">🐟</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {speciesLabel(b.species)}
                      </div>
                      <div className="text-lg font-bold text-fishing-blue">{b.lengthCm}cm</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

        <AchievementsBadges catches={allCatches as CatchRecord[]} placements={placements} />

        {/* My Catches summary (full width) */}
        {allCatches.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-8">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">🎣 My Catches</h3>
              <button
                onClick={() => {
                  window.history.pushState({}, '', '/my-fishing');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }}
                className="text-sm text-fishing-blue hover:underline"
              >
                View all →
              </button>
            </div>
            <p className="text-sm text-gray-600">
              You have <span className="font-semibold">{allCatches.length}</span> approved{' '}
              {allCatches.length === 1 ? 'catch' : 'catches'} this season.
            </p>
          </div>
        )}

        {/* My Happy Snaps — caller + family members (self-hides if none) */}
        {userId && (
          <MyHappySnapsCard
            memberSubs={[
              userId,
              ...(familyMembersList.map((fm: any) => fm.cognitoSub).filter(Boolean) as string[]),
            ]}
          />
        )}

        {/* BAND 4 — COMMUNITY: recent club catches + family */}
        {(recentClubCatches.length > 0 ||
          familyMembersList.length > 0 ||
          !(member.familyGroupId && !member.familyPrimary)) && (
          <>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Community
            </h2>

            {recentClubCatches.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">🎣 Recent Club Catches</h3>
                <div className="space-y-3">
                  {recentClubCatches.map((c: any) => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                    >
                      <div>
                        <span className="font-medium text-gray-900">
                          {c.competitorName || 'Unknown'}
                        </span>
                        <span className="text-gray-500 mx-2">caught a</span>
                        <span className="font-medium text-fishing-blue">
                          {speciesLabel(c.species)}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                        {c.submissionTimestamp
                          ? formatClubDate(c.submissionTimestamp)
                          : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* #127 slice 5 (R6, R10): "Add a family member" for the primary
                or an individual (dependants never see this). Rendered even
                when familyMembersList is empty so an individual can start a
                family. */}
            {!(member.familyGroupId && !member.familyPrimary) && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">👨‍👩‍👧‍👦 Family</h3>
                  <button
                    type="button"
                    onClick={() => setShowAddFamilyMember(true)}
                    className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-fishing-blue hover:bg-blue-700 rounded-md whitespace-nowrap"
                  >
                    Add a family member
                  </button>
                </div>
              </div>
            )}

            {familyMembersList.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">👨‍👩‍👧‍👦 Family Members</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {familyMembersList.map((fm: any) => {
                    const results = familyResults[fm.id];
                    const canRequestRemoval = !!member.familyGroupId && member.familyPrimary === true;
                    const canRequestPortalAccess =
                      !!member.familyGroupId && member.familyPrimary === true && !fm.cognitoSub;
                    const pendingPortalAccessRequest = familyRequests.find(
                      (r) =>
                        r.kind === 'PORTAL_ACCESS' &&
                        r.status === 'PENDING' &&
                        r.targetMemberId === fm.id
                    );
                    return (
                      <div key={fm.id} className="border border-gray-200 rounded-lg p-4">
                        <p className="font-semibold text-gray-900">
                          {fm.firstName} {fm.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{fm.memberNumber || 'Pending'}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {fm.familyRole === 'CHILD' ? 'Junior' : 'Adult'}
                        </p>
                        {canRequestPortalAccess &&
                          (pendingPortalAccessRequest ? (
                            <p className="mt-2 text-sm font-medium text-amber-700">
                              Login requested: waiting for approval
                            </p>
                          ) : (
                            <button
                              type="button"
                              ref={(el) => {
                                portalAccessButtonRefs.current[fm.id] = el;
                              }}
                              onClick={() => setPortalAccessTarget(fm)}
                              aria-label={`Ask for a login for ${fm.firstName} ${fm.lastName}`}
                              className="mt-2 min-h-[44px] text-sm font-medium text-fishing-blue hover:underline"
                            >
                              Ask for a login
                            </button>
                          ))}
                        {canRequestRemoval && (
                          <button
                            type="button"
                            onClick={() => setRemovalTarget(fm)}
                            className="mt-2 min-h-[44px] text-sm font-medium text-red-700 hover:underline"
                          >
                            Request removal
                          </button>
                        )}
                        {results && (
                          <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">
                            <p className="text-sm text-gray-700">
                              <span className="font-semibold">{results.catchCount}</span> approved{' '}
                              {results.catchCount === 1 ? 'catch' : 'catches'} this season
                            </p>
                            {results.bests.length > 0 && (
                              <p className="text-xs text-gray-500">
                                Best:{' '}
                                {results.bests
                                  .map((b) => `${speciesLabel(b.species)} ${b.lengthCm}cm`)
                                  .join(', ')}
                              </p>
                            )}
                            {results.rank !== null && (
                              <p className="text-xs text-gray-500">
                                Season points: <span className="font-medium">{results.points}</span>{' '}
                                (rank #{results.rank})
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* #127 slice 5 (R6.6, R10.7): status list for the caller's own
            requests, and consent cards for requests awaiting their
            response. Rendered unconditionally (outside the Community band
            gate above) so a dependant awaiting consent sees their card even
            when they have no family roster, recent catches, or Add access. */}
        <FamilyRequestsList requests={familyRequests} onConsentResolved={loadFamilyRequests} />

        {/* BAND 5 — CHAMPIONSHIP */}
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Season championship
        </h2>
        {userId &&
          (() => {
            const isJunior = member?.isJunior === true;
            const standings = isJunior
              ? seasonStandings.juniorStandings
              : seasonStandings.seniorStandings;
            const divLabel = isJunior ? 'Junior' : 'Senior';
            const myPoints = findMemberPoints(standings, userId);
            const myRank = findMemberRank(standings, userId);
            return (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-8">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">🏆 Your Season Points</h3>
                  <button
                    onClick={() => {
                      window.history.pushState({}, '', '/season-championship');
                      window.dispatchEvent(new PopStateEvent('popstate'));
                    }}
                    className="text-sm text-fishing-blue hover:underline"
                  >
                    Full standings →
                  </button>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-fishing-blue">{myPoints}</div>
                    <div className="text-xs text-gray-500">Total Points</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900">
                      {myRank ? `#${myRank}` : 'Unranked'}
                    </div>
                    <div className="text-xs text-gray-500">{divLabel} Division</div>
                  </div>
                </div>
              </div>
            );
          })()}

        {/* BAND 6 — TIDE & WEATHER (bottom of page) */}
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Tide & weather
        </h2>
        <div className="mb-8">
          <TideWeatherWidget />
        </div>
      </div>

      {checkInEventId && member && (
        <CheckInModal
          eventId={checkInEventId}
          member={member}
          familyMembers={familyMembersList}
          onClose={() => {
            setCheckInEventId(null);
            window.history.replaceState({}, '', '/dashboard');
          }}
          onSuccess={() => {
            setCheckInEventId(null);
            window.history.replaceState({}, '', '/dashboard');
            // Reload to refresh "My Registrations"
            fetchMemberData();
          }}
        />
      )}

      {/* #127 slice 5 — "Add a family member" (R6, design §7.2–§7.3). */}
      {showAddFamilyMember && member && (
        <AddFamilyMemberDialog
          own={member}
          onClose={() => setShowAddFamilyMember(false)}
          onDone={(message) => {
            setShowAddFamilyMember(false);
            setFamilyActionToast(message);
            loadFamilyRequests();
          }}
        />
      )}

      {/* #127 slice 5 — "Request removal" (R10, design §7.4). */}
      {removalTarget && member && (
        <RequestRemovalDialog
          own={member}
          member={removalTarget}
          otherFamilyMembers={familyMembersList.filter((fm: any) => fm.id !== removalTarget.id)}
          onClose={() => setRemovalTarget(null)}
          onDone={(message) => {
            setRemovalTarget(null);
            setFamilyActionToast(message);
            loadFamilyRequests();
          }}
        />
      )}

      {/* #150 slice 2 — "Ask for a login" (R6, design §6). Focus returns to
          the button that opened it, same pattern as EditMemberModal's
          "Set password now" dialog. */}
      {portalAccessTarget && (
        <RequestPortalAccessDialog
          member={portalAccessTarget}
          onClose={() => {
            const targetId = portalAccessTarget.id;
            setPortalAccessTarget(null);
            setTimeout(() => portalAccessButtonRefs.current[targetId]?.focus(), 0);
          }}
          onDone={(message) => {
            const targetId = portalAccessTarget.id;
            setPortalAccessTarget(null);
            setFamilyActionToast(message);
            loadFamilyRequests();
            setTimeout(() => portalAccessButtonRefs.current[targetId]?.focus(), 0);
          }}
        />
      )}

      {familyActionToast && (
        <div
          role="alert"
          aria-live="assertive"
          className="fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm transition-all max-w-sm bg-fishing-blue"
        >
          {familyActionToast}
          <button
            type="button"
            onClick={() => setFamilyActionToast(null)}
            className="ml-3 underline"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
};

export default MemberDashboard;
